import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DerivativeResults } from '@starflow/app/modules/derivative';
import cs from 'classnames';
import { useDebounceEffect } from 'ahooks';
import { useDerivativeTasks, DerivativeResultActions } from '@starflow/app/modules/derivative';
import DragInner from '../DragInner';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { MAX_IMGE_WH, MIN_IMAGE_WH, resizeWrapperId } from '../../../constants';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { DerivativeQueue, DerivativeTask } from '@starflow/app/modules/derivative';
import { setProjectState, useProjectState } from '@starflow/app/store';
import { translate } from '@shakkerai/util-i18n';
import { findMatchedResultIndex } from '@starflow/app/helpers/flow';
import './index.scss';

const borderW = 0;
const handerWH = 160;
const HandlerType = {
  topLeft: 'top-left',
  topRight: 'top-right',
  bottomLeft: 'bottom-left',
  bottomRight: 'bottom-right',
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};
const handlers = [
  HandlerType.topLeft,
  HandlerType.topRight,
  HandlerType.bottomLeft,
  HandlerType.bottomRight,
  HandlerType.top,
  HandlerType.bottom,
  HandlerType.left,
  HandlerType.right,
];

const ResizeWrapper = ({ element, unit, isMiniMode, scale, generator }) => {
  const currentNodeId = useProjectState('currentNodeId');
  const unitId = element.lastDerivativeUnitId;
  const tasks = useDerivativeTasks(unitId);
  const matchedResultIndex = findMatchedResultIndex(unitId);
  const [selectedIndex, setSelectedIndex] = useState(unit.result?.selectedIndex ?? matchedResultIndex); // -1: 原图, >=0: 选择的任务索引

  const resultsRef = useRef(null);
  const dragElement = useRef();
  const dragInner = useRef();

  const handleCancel = () => {
    resultsRef.current?.handleSelect(-1);
  };

  useDebounceEffect(
    () => {
      if (tasks?.length) return;
      const { width, height } = element;
      let right = 0;
      let bottom = 0;
      if (height > width) {
        right = height - width;
      } else {
        bottom = width - height;
      }
      const newWrapper = {
        width: Math.max(height, width),
        height: Math.max(height, width),
      };
      updateUnitData(generator.id, {
        outpaintRect: {
          wrapper: newWrapper,
          inner: {
            width: width,
            height: height,
          },
          relativeXY: {
            left: 0,
            right: right,
            top: 0,
            bottom: bottom,
          },
        },
      });
    },
    [element],
    { wait: 400 },
  );

  // 这段代码留着，可能需要实现选框的拖拽
  // const startDrag = (event) => {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   const currentHandle = event.target;
  //   const isResizeHandle = currentHandle.className.includes('resize-handle');
  //   if (isResizeHandle) return;
  //   const startX = event.clientX;
  //   const startY = event.clientY;
  //   const startLeft = dragElement.current.offsetLeft;
  //   const startTop = dragElement.current.offsetTop;
  //   document.addEventListener('mousemove', drag);
  //   document.addEventListener('mouseup', stopDrag);
  //   function drag(event) {
  //     const dx = event.clientX - startX;
  //     const dy = event.clientY - startY;
  //     const newLeft = startLeft + dx;
  //     const newTop = startTop + dy;
  //     dragElement.current.style.left = newLeft + 'px';
  //     dragElement.current.style.top = newTop + 'px';
  //   }
  //   function stopDrag() {
  //     document.removeEventListener('mousemove', drag);
  //     document.removeEventListener('mouseup', stopDrag);
  //   }
  // };
  // const { run: setOffsetToOutpaintImg } = useDebounceFn(
  //   () => {
  //     const inner = domRectToObject(dragInner.current.getRectInfo());
  //     const wrapper = getDragWrapperInfo();
  //     const warpperObj = {
  //       ...wrapper,
  //       width: wrapper.width / scale,
  //       height: wrapper.height / scale,
  //     };
  //     const innerObj = {
  //       ...inner,
  //       width: inner.width / scale,
  //       height: inner.height / scale,
  //     };
  //     updateUnitData(generator.id, {
  //       outpaintRect: {
  //         wrapper: warpperObj,
  //         inner: innerObj,
  //         relativeXY: {
  //           left: (inner.left - wrapper.left) / scale,
  //           right: (wrapper.left + wrapper.width - inner.left - inner.width) / scale,
  //           top: (inner.top - wrapper.top) / scale,
  //           bottom: (wrapper.top + wrapper.height - inner.top - inner.height) / scale,
  //         },
  //       },
  //     });
  //   },
  //   {
  //     wait: 400,
  //   },
  // );
  const startResize = (event, arrow) => {
    event.preventDefault();
    const direction = arrow;
    const startX = event.clientX;
    const startY = event.clientY;
    const wrapper = getDragWrapperInfo();
    const startWidth = wrapper.width;
    const startHeight = wrapper.height;
    const { width: innerW, height: innerH } = dragInner.current.getRectInfo();
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
    function resize(event) {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      let width = startWidth,
        height = startHeight;
      if (direction.includes('left')) {
        width = startWidth - dx;
      }
      if (direction.includes('right')) {
        width = startWidth + dx;
      }
      if (direction.includes('top')) {
        height = startHeight - dy;
      }
      if (direction.includes('bottom')) {
        height = startHeight + dy;
      }
      const lastMinW = Math.max(Math.round(innerW / scale), MIN_IMAGE_WH);
      const lastMinH = Math.max(Math.round(innerH / scale), MIN_IMAGE_WH);
      if (Math.round(width / scale) < lastMinW || Math.round(width / scale) >= MAX_IMGE_WH) return;
      if (Math.round(height / scale) < lastMinH || Math.round(height / scale) >= MAX_IMGE_WH) return;

      dragElement.current.style.height = height + 'px';
      dragElement.current.style.width = width + 'px';
      dragInner.current.refreshLocation(dragElement.current.getBoundingClientRect());
      // setOffsetToOutpaintImg(); // 这里收敛到内部img去做计算，保留一段时间
    }
    function stopResize() {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    }
  };

  const getDragWrapperInfo = () => {
    const wrapper = dragElement.current.getBoundingClientRect();
    return {
      left: wrapper.left + borderW,
      top: wrapper.top + borderW,
      width: wrapper.width - borderW * 2,
      height: wrapper.height - borderW * 2,
    };
  };

  useEffect(() => {
    setProjectState({
      outPaintResultSelectedIndex: selectedIndex,
    });
  }, [selectedIndex]);

  if (!generator.data.data?.outpaintRect) {
    return null;
  }

  const dealHandlerStyle = (scale, arrow) => {
    switch (true) {
      case arrow === HandlerType.top || arrow === HandlerType.bottom:
        return {
          left: `calc(50% - ${(handerWH * scale) / 2}px)`,
        };
      case arrow === HandlerType.left || arrow === HandlerType.right:
        return {
          top: `calc(50% - ${(handerWH * scale) / 2}px)`,
        };
    }
  };
  const invisibleHandlerStyle = (scale, arrow) => {
    switch (arrow) {
      case HandlerType.left:
        return {
          left: `-${(handerWH * scale) / 2}px`,
          top: `0px`,
          opacity: 0,
        };
      case HandlerType.bottom:
        return {
          left: `0px`,
          top: `${(handerWH * scale) / 2}px`,
          opacity: 0,
        };
      case HandlerType.top:
        return {
          left: `0px`,
          top: `-${(handerWH * scale) / 2}px`,
          opacity: 0,
        };
      case HandlerType.right:
        return {
          left: `${(handerWH * scale) / 2}px`,
          top: 0,
          opacity: 0,
        };

      case HandlerType.bottomLeft:
        return {
          left: `-${(handerWH * scale) / 2}px`,
          top: `${(handerWH * scale) / 2}px`,
          opacity: 0,
          width: `${handerWH * scale}px`,
          height: `${handerWH * scale}px`,
        };
      case HandlerType.bottomRight:
        return {
          left: `${(handerWH * scale) / 2}px`,
          top: `${(handerWH * scale) / 2}px`,
          opacity: 0,
        };
      case HandlerType.topLeft:
        return {
          left: `-${(handerWH * scale) / 2}px`,
          top: `-${(handerWH * scale) / 2}px`,
          opacity: 0,
        };
      case HandlerType.topRight:
        return {
          left: `${(handerWH * scale) / 2}px`,
          top: `-${(handerWH * scale) / 2}px`,
          opacity: 0,
        };
    }
  };

  const {
    wrapper: { width: wrapperW, height: wrapperH },
    inner: { width: innerW, height: innerH },
    relativeXY: { left, top },
  } = generator.data.data.outpaintRect;

  const selectedItem = tasks[selectedIndex];

  const calcStyle = () => {
    if (!selectedItem || selectedItem?.type === 'queue' || selectedItem?.type === 'task') {
      return { width: wrapperW * scale, height: wrapperH * scale };
    }
    if (selectedItem?.url) {
      return { width: selectedItem.width * scale, height: selectedItem.height * scale };
    }
    return {};
  };
  return (
    <div
      id={!selectedItem ? resizeWrapperId : ''}
      className={cs(
        'relative resize-container pointer-events-auto inset-0.5 box-content border-primary bg-[#DDECFF] translate-x-[-4px] translate-y-[-4px]',
        !selectedItem ? '' : 'border-2',
      )}
      style={calcStyle()}
      ref={!selectedItem ? dragElement : null}
      // onMouseDown={(e) => startDrag(e)}
    >
      <span
        className={cs(
          'absolute flex items-center h-4 px-4 bg-primary whitespace-nowrap rounded-t-sm text-[10px] text-white',
        )}
        style={{ top: !selectedItem ? '-21px' : '-18px', left: !selectedItem ? '-4px' : '-2px' }}
      >
        <span>
          {unit.label} - {translate('imagetool_outpaint')}
        </span>
      </span>
      {!isMiniMode && (
        <DerivativeResults
          ref={resultsRef}
          className="z-10 translate-x-[-6px]"
          items={tasks}
          elementId={element.id}
          flowId={element.flowId}
          selected={selectedIndex}
          onSelect={setSelectedIndex}
          sourceElement={unit.data?.sourceElement || element}
          originlUrl={element.originalUrl || element.url}
          currentNodeId={currentNodeId}
        />
      )}
      {!selectedItem
        ? handlers.map((arrow) => (
            <span
              key={arrow}
              data-set="handler"
              className={cs(arrow, 'resize-handle pointer-events-auto relative invisibleHandler')}
              style={{
                width: `${handerWH * scale}px`,
                height: `${handerWH * scale}px`,
                ...dealHandlerStyle(scale, arrow),
              }}
              onMouseDown={(e) => startResize(e, arrow)}
            >
              <i
                className={cs('block bg-black invisibleHandler absolute')}
                style={{
                  width: `${handerWH * scale}px`,
                  height: `${handerWH * scale}px`,
                  ...invisibleHandlerStyle(scale, arrow),
                }}
              />
            </span>
          ))
        : null}
      {!selectedItem ? (
        <div className="w-full h-full">
          <DragInner
            getDragWrapperInfo={getDragWrapperInfo}
            ref={dragInner}
            width={innerW * scale}
            height={innerH * scale}
            left={left * scale}
            top={top * scale}
            dragElement={dragElement}
            element={element}
            generator={generator}
          />
        </div>
      ) : null}
      <div className=" h-6 absolute bottom-[-30px] right-[-4px] bg-[#7a7a7b] text-xs text-white flex items-center justify-center px-2">
        {Math.round(wrapperW)} X {Math.round(wrapperH)}
      </div>
      {selectedItem?.type === 'queue' ? <DerivativeQueue data={selectedItem} /> : null}
      {selectedItem?.type === 'task' ? <DerivativeTask data={selectedItem} /> : null}
      {selectedItem?.url ? (
        <img src={convert2CompressedUrl(selectedItem.url, 2048)} className="absolute inset-0 w-full h-full" />
      ) : null}
    </div>
  );
};

export default ResizeWrapper;
